import { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Checkbox, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import {
  ApiCreateDocumentGenerationJob,
  ApiService,
  CompanyDocuments,
  CompanyService,
  GenerationJobsService,
  RetrieveDocumentGenerationJob,
} from 'api';
import { useStore } from 'common/store';
import { FormDialog } from 'components';
import { useDocuments, useResponse } from 'services/hooks';
import DocumentGenerationJobsCreateModalProps from './DocumentGenerationJobCreateModal.props';
import { useAsyncProcessor } from '../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { Logger } from '../../../../utilities';
import { CREATE_MODAL_CONSTANTS, QUERY_CONSTANTS } from '../../constants';

const DocumentGenerationJobCreateModal = ({ ...rest }: DocumentGenerationJobsCreateModalProps) => {
  const queryClient = useQueryClient();

  const [preCreatedJob, setPreCreatedJob] = useState<ApiCreateDocumentGenerationJob>({
    reference_files: [],
    processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
    desired_termination_state: 'COMPLETE',
    parser_overrides: [],
  });
  const { errorNotification } = useResponse();
  const { addJob, removeJob } = useAsyncProcessor();

  const { apiCompanyDocumentGenerationJobsNewCreate } = ApiService;
  const { getDocuments, companyDocuments } = useDocuments() as unknown as {
    getDocuments: (companyId: number, measurementDataId: number) => Promise<void>;
    companyDocuments: CompanyDocuments[];
  };

  // any as use store is not typed
  const [{ companyInfo }] = useStore() as any;

  const memoizedGetDocuments = useMemo(() => getDocuments, [getDocuments]);

  const configureJobByType = useCallback((): ApiCreateDocumentGenerationJob => {
    switch (preCreatedJob.processing_type) {
      case ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE:
        return {
          ...preCreatedJob,
          parser_overrides: [
            {
              file_type: 'xlsx',
              xlsx_parsing_mode: 'JSON_CONVERSION',
              minify_json: true,
            },
          ],
        };
      default:
        return preCreatedJob;
    }
  }, [preCreatedJob]);

  const handleCreate = useCallback(async () => {
    try {
      const jobToCreate = configureJobByType();
      const createdJob = await apiCompanyDocumentGenerationJobsNewCreate(companyInfo?.id, jobToCreate as any);

      queryClient.invalidateQueries({
        queryKey: [QUERY_CONSTANTS.getDocumentGenerationJobs, companyInfo.id, 0],
        exact: false,
      });

      // If it's a FINANCIAL_MAPPING job, add to polling
      if (
        preCreatedJob.processing_type === ApiCreateDocumentGenerationJob.processing_type.FINANCIAL_MAPPING &&
        createdJob?.job_id
      ) {
        Logger.info(
          'DocumentGenerationJobCreateModal:',
          `Adding FINANCIAL_MAPPING job ${createdJob.job_id} to polling`
        );

        addJob({
          id: createdJob.job_id,
          details: {
            title: 'Financial Mapping',
            description: `Creating mapping for ${createdJob.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyInfo.id.toString(),
                createdJob.job_id
              );
              return result;
            },
            pollingInterval: 3000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
              try {
                // Auto-create the mapping
                await GenerationJobsService.apiGenerationJobsAttributeMappingsCreate(result.job_id);
                enqueueSnackbar('Financial mapping completed and created', { variant: 'success' });

                // Invalidate queries to refresh the UI
                queryClient.invalidateQueries({
                  queryKey: [QUERY_CONSTANTS.getDocumentGenerationJobs, companyInfo.id, 0],
                  exact: false,
                });
              } catch (error) {
                Logger.error('DocumentGenerationJobCreateModal - Error creating mapping for completed job', error);
                enqueueSnackbar('Job completed but mapping creation failed', { variant: 'warning' });
              }
              removeJob(result.job_id);
            } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error(
              `DocumentGenerationJobCreateModal - Generation job ${createdJob.job_id} polling error: ${error.message}`,
              error
            );
          },
        });
      }

      // any as the exception is not typed
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error;
      errorNotification(errorMessage || CREATE_MODAL_CONSTANTS.ERROR);
    }
  }, [
    preCreatedJob,
    companyInfo,
    queryClient,
    apiCompanyDocumentGenerationJobsNewCreate,
    errorNotification,
    addJob,
    removeJob,
    configureJobByType,
  ]);

  useEffect(() => {
    const getData = async () => {
      await memoizedGetDocuments(companyInfo.id, companyInfo.measurement_data_id);
    };

    getData();
    // need this disable getDocuments has a rerendering issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  return (
    <FormDialog
      customButtonLabel={CREATE_MODAL_CONSTANTS.ACTION_BUTTON}
      onClose={() => rest.onClose && rest.onClose({}, 'backdropClick')}
      open={rest.open}
      title={CREATE_MODAL_CONSTANTS.TITLE}
      onSave={handleCreate}
      isValid={preCreatedJob.reference_files.length > 0 && !!preCreatedJob.processing_type}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div>
          <Typography variant="h3">{CREATE_MODAL_CONSTANTS.DOCUMENT_ACTION}</Typography>
          {companyDocuments && companyDocuments.length > 0 ? (
            <Autocomplete
              multiple
              id="checkboxes-documents"
              options={companyDocuments.flatMap(doc => doc.files || [])}
              disableCloseOnSelect
              onChange={(_, v) =>
                setPreCreatedJob({
                  ...preCreatedJob,
                  reference_files: v?.map(file => file?.file.id?.toString()) as any,
                })
              }
              getOptionLabel={option => option?.file?.filename ?? 'Unknown file'}
              getOptionKey={option => option.document_reference_id}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option?.file ? `${option.file.file_path}/${option.file.filename}.${option.file.file_type}` : 'Unknown file'}
                  </li>
                );
              }}
              style={{ width: '100%' }}
              renderInput={params => (
                <TextField
                  variant="outlined"
                  {...params}
                  placeholder={CREATE_MODAL_CONSTANTS.DOCUMENT_SELECT_PLACEHOLDER}
                />
              )}
              isOptionEqualToValue={(option, value) => option.document_reference_id === value.document_reference_id}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No documents available for selection.
            </Typography>
          )}
        </div>
        <div>
          <Typography variant="h3">{CREATE_MODAL_CONSTANTS.TYPE_ACTION}</Typography>
          <Select
            value={preCreatedJob.processing_type}
            onChange={e =>
              setPreCreatedJob(prev => ({
                ...prev,
                processing_type: e.target.value as ApiCreateDocumentGenerationJob.processing_type,
              }))
            }
            variant="outlined"
            style={{ width: '100%' }}>
            {CREATE_MODAL_CONSTANTS.OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    </FormDialog>
  );
};

export default DocumentGenerationJobCreateModal;
